import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AlertTriangle, ArrowLeft, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SelectMenu } from '@/components/ui/select-menu';
import { MultiSelectMenu } from '@/components/ui/multi-select-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  BUCKETS,
  ITEM_PATHS,
  ITEM_PATH_LABELS,
  MULTIPLIER_MAX,
  MULTIPLIER_MIN,
  MULTI_VALUED_ITEM_PATHS,
  OUTCOME_MODES,
  TARGET_MODES,
  toApiRule,
  toFormValues,
  toRuleKey,
} from './constants';
import {
  emptyProfileNote,
  excludeSummary,
  pairingWarning,
  staleValuesWarning,
  staleWorkspacesWarning,
} from './rule-advisories';
import OutcomeField from './outcome-field';

const outcomeSchema = z
  .object({
    mode: z.nativeEnum(OUTCOME_MODES),
    multiplier: z.union([z.string(), z.number()]).optional(),
  })
  .superRefine((outcome, ctx) => {
    if (outcome.mode !== OUTCOME_MODES.MULTIPLIER) return;
    const numeric = Number(outcome.multiplier);
    const issue = message =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['multiplier'], message });

    if (outcome.multiplier === '' || outcome.multiplier === undefined || Number.isNaN(numeric)) {
      issue('Enter a multiplier, or switch to "No change".');
      return;
    }
    if (numeric < MULTIPLIER_MIN || numeric > MULTIPLIER_MAX) {
      issue(
        `Out of the supported range — must be between ${MULTIPLIER_MIN} and ${MULTIPLIER_MAX}.`,
      );
    }
  });

/**
 * Takes the org's configured profile fields, because `userPath` is only valid
 * relative to them — the backend accepts any string and silently never matches.
 */
const ruleSchema = allowedUserPaths =>
  z
    .object({
      key: z.string().trim().min(1, 'Every rule needs a name.'),
      enabled: z.boolean(),
      itemPath: z.nativeEnum(ITEM_PATHS, {
        required_error: 'Every rule needs a field to look at.',
      }),
      targetMode: z.nativeEnum(TARGET_MODES),
      userPath: z.string().optional(),
      values: z.array(z.string()),
      vocabulary: z.array(z.string()),
      workspaceIds: z.array(z.string()),
      match: outcomeSchema,
      unassigned: outcomeSchema,
      other: outcomeSchema,
    })
    .superRefine((rule, ctx) => {
      if (rule.targetMode === TARGET_MODES.USER) {
        const message = !rule.userPath
          ? 'Pick the profile field to read the target values from.'
          : !allowedUserPaths.has(rule.userPath)
            ? 'This organization has no options configured for that field, so the rule would never fire. Pick one of the listed fields, or match against a fixed list instead.'
            : null;
        if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['userPath'], message });
      }
      if (rule.targetMode === TARGET_MODES.STATIC && !rule.values.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['values'],
          message: 'A rule needs something to look for — pick at least one value.',
        });
      }
    });

const Section = ({ title, hint, children }) => (
  <section className="space-y-4 rounded-lg border p-4">
    <div>
      <h4 className="text-sm font-bold">{title}</h4>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
    {children}
  </section>
);

/** Caveats that matter when you hit them, but are noise on every render. */
const Hint = ({ children }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-muted-foreground hover:text-foreground align-middle">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const Advisory = ({ advisory }) => {
  if (!advisory) return null;
  const isWarning = advisory.level === 'warning';
  const Icon = isWarning ? AlertTriangle : Info;
  return (
    <p
      className={
        isWarning
          ? 'flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-700'
          : 'text-muted-foreground flex items-start gap-2 text-sm'
      }>
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{advisory.message}</span>
    </p>
  );
};

/**
 * Create/edit view for a single rule, laid out as the two halves of the rule
 * itself: what it looks at, and what it does about it. Nothing is written to the
 * server here — `onSave` hands the built rule back to the sheet's draft array.
 */
export default function RuleEditor({ rule, existingKeys, options, onSave, onCancel }) {
  const [pendingExclude, setPendingExclude] = useState(null);

  const schema = useMemo(
    () => ruleSchema(new Set(options.userPathOptions.map(option => option.value))),
    [options.userPathOptions],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(rule, options.userPathOptions),
  });

  const [itemPath, targetMode, userPath, values, workspaceIds] = form.watch([
    'itemPath',
    'targetMode',
    'userPath',
    'values',
    'workspaceIds',
  ]);

  const valueOptions = options.valueOptionsFor(itemPath);
  const supportsVocabulary = MULTI_VALUED_ITEM_PATHS.has(itemPath);

  const previewRule = useMemo(
    () =>
      toApiRule({
        ...form.getValues(),
        itemPath,
        targetMode,
        userPath,
        values: values ?? [],
        workspaceIds: workspaceIds ?? [],
      }),
    [form, itemPath, targetMode, userPath, values, workspaceIds],
  );

  const advisories = [
    pairingWarning(previewRule, options),
    staleValuesWarning(previewRule, valueOptions),
    staleWorkspacesWarning(previewRule, options.workspaceOptions),
    emptyProfileNote(previewRule, options.userPathLabel(previewRule.userPath)),
  ].filter(Boolean);

  const duplicateKey = existingKeys.includes(form.watch('key').trim().toUpperCase());

  const itemPathOptions = Object.values(ITEM_PATHS).map(value => ({
    value,
    label: ITEM_PATH_LABELS[value],
    hint: value,
  }));

  const commit = formValues => onSave(toApiRule(formValues));

  const onSubmit = formValues => {
    const apiRule = toApiRule(formValues);
    const summary = excludeSummary(apiRule, options);
    if (summary) {
      setPendingExclude({ summary, formValues });
      return;
    }
    commit(formValues);
  };

  if (pendingExclude) {
    return (
      <div className="space-y-4 p-6">
        <h3 className="text-destructive flex items-center gap-2 text-lg font-bold">
          <AlertTriangle className="h-5 w-5" />
          This rule hides content
        </h3>
        <div className="border-destructive/40 bg-destructive/5 space-y-3 rounded-md border p-4 text-sm">
          {pendingExclude.summary.lines.map(line => (
            <p key={line}>{line}</p>
          ))}
          {previewRule.userPath && (
            <p className="text-muted-foreground">
              Users with no {options.userPathLabel(previewRule.userPath)} on their profile are
              unaffected — the rule is skipped entirely for them.
            </p>
          )}
          <p className="text-muted-foreground">
            This is not access control. Excluded documents stay reachable by direct link and through
            browsing to anyone permitted to see them.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setPendingExclude(null)}>
            Back to editing
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => commit(pendingExclude.formValues)}>
            Yes, hide these results
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back to rules
          </button>

          <div className="flex items-start gap-4">
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <FormLabel className="font-bold whitespace-nowrap">
                      Name
                      <Hint>
                        Appears in server logs and has no effect on behaviour. Names are not
                        enforced unique, but duplicates make the logs ambiguous.
                      </Hint>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="POLICIES_BY_SITE"
                        className="flex-1"
                        onBlur={() => field.onChange(toRuleKey(field.value))}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                  {duplicateKey && (
                    <p className="text-sm text-amber-700">
                      Another rule already uses this name — logs will be ambiguous.
                    </p>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex h-10 items-center gap-2 space-y-0">
                  <FormLabel className="font-bold">Enabled</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section
              title="What it looks at"
              hint="One field on each search result, and which of its values count as a match.">
              <FormField
                control={form.control}
                name="itemPath"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Field</FormLabel>
                    <FormControl>
                      <SelectMenu
                        value={field.value}
                        onValueChange={next => {
                          field.onChange(next);
                          // Both lists are drawn from this field's vocabulary.
                          form.setValue('values', [], { shouldDirty: true });
                          form.setValue('vocabulary', [], { shouldDirty: true });
                        }}
                        options={itemPathOptions}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="targetMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match against</FormLabel>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value === TARGET_MODES.USER ? 'default' : 'outline'}
                        onClick={() => field.onChange(TARGET_MODES.USER)}>
                        The user&apos;s profile
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value === TARGET_MODES.STATIC ? 'default' : 'outline'}
                        onClick={() => field.onChange(TARGET_MODES.STATIC)}>
                        A fixed list
                      </Button>
                    </div>
                    <FormDescription className="text-xs">
                      {field.value === TARGET_MODES.USER
                        ? 'One rule covers every value — each user gets their own content first.'
                        : 'Every user in the organization gets the same treatment.'}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {targetMode === TARGET_MODES.USER ? (
                <FormField
                  control={form.control}
                  name="userPath"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Profile field
                        <Hint>
                          Read from the searcher&apos;s subscription in this organization. Only
                          fields this organization has configured are listed.
                        </Hint>
                      </FormLabel>
                      <FormControl>
                        <SelectMenu
                          value={field.value}
                          onValueChange={field.onChange}
                          options={options.userPathOptions}
                          placeholder="Select a profile field"
                          emptyLabel="This organization has no profile fields configured"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="values"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Values
                        <Hint>
                          Matching is an exact, case-sensitive comparison on the code, so values are
                          picked from this organization&apos;s vocabulary rather than typed.
                        </Hint>
                      </FormLabel>
                      <FormControl>
                        <MultiSelectMenu
                          value={field.value}
                          onChange={field.onChange}
                          options={valueOptions}
                          searchable={valueOptions.length > 8}
                          placeholder="Pick one or more values"
                          emptyLabel="No values available for this field"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="workspaceIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Workspaces
                      <Hint>
                        Leave empty to apply org-wide. The check is against the item&apos;s own
                        workspace, so content that lives elsewhere and is linked into a scoped
                        workspace is not affected.
                      </Hint>
                    </FormLabel>
                    <FormControl>
                      <MultiSelectMenu
                        value={field.value}
                        onChange={field.onChange}
                        options={options.workspaceOptions}
                        placeholder="Whole organization"
                        emptyLabel="No workspaces found"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {supportsVocabulary && (
                <Collapsible className="rounded-md border p-3">
                  <CollapsibleTrigger className="text-sm font-bold">
                    Advanced — which values does this rule consider?
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <FormField
                      control={form.control}
                      name="vocabulary"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <MultiSelectMenu
                              value={field.value}
                              onChange={field.onChange}
                              options={valueOptions}
                              searchable={valueOptions.length > 8}
                              placeholder="Leave empty unless you know you need it"
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Empty means &quot;Unassigned&quot; is anything with no{' '}
                            {ITEM_PATH_LABELS[itemPath].toLowerCase()} at all. Set it when items
                            also carry unrelated values — otherwise an item tagged for something
                            else, but not for anything this rule cares about, counts as assigned and
                            lands in Other.
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}
            </Section>

            <Section
              title="What it does"
              hint="Every result falls into exactly one bucket. Match is checked first.">
              {BUCKETS.map(bucket => (
                <OutcomeField key={bucket} bucket={bucket} />
              ))}
            </Section>
          </div>

          {advisories.map(advisory => (
            <Advisory key={advisory.message} advisory={advisory} />
          ))}
        </div>

        <div className="flex justify-end gap-2 border-t p-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">{rule ? 'Update rule' : 'Add rule'}</Button>
        </div>
      </form>
    </Form>
  );
}
